import { useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { ConfirmModal } from "./ConfirmModal";

const NAV = [
  { to: "/dashboard",  label: "Inicio",      icon: "🏠", match: ["/dashboard"] },
  { to: "/biblioteca", label: "Biblioteca",  icon: "📚", match: ["/biblioteca"] },
  { to: "/comunidad",  label: "Comunidad",   icon: "💬", match: ["/comunidad"] },
  { to: "/perfil",     label: "Mi perfil",   icon: "👤", match: ["/perfil"] },
];

function isActive(pathname: string, match: string[]) {
  return match.some((m) => pathname === m || pathname.startsWith(`${m}/`));
}

interface PrivateLayoutProps {
  children: ReactNode;
  /** Ancho máximo del contenido; algunas pantallas (foro/perfil) usan columnas más angostas */
  maxWidth?: number;
}

export function PrivateLayout({ children, maxWidth }: PrivateLayoutProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, clearSession } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  function doLogout() {
    clearSession();
    navigate("/");
  }

  const firstName = user?.nombre.split(" ")[0] ?? "Tu cuenta";
  const planLabel = user?.plan === "premium" ? "⭐ Premium" : "Plan gratuito";
  const initial = (user?.nombre || "U")[0].toUpperCase();
  const fotoUrl = user?.foto_url ?? user?.avatar_url ?? null;

  return (
    <div className="app-shell">
      {/* SIDEBAR — desktop */}
      <aside className="sidebar">
        <Link to="/dashboard" className="sidebar__logo">
          <img src="/logo-psicodosis.png" alt="PzicoDosis" />
          <span>PzicoDosis</span>
        </Link>

        <nav className="sidebar__nav">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`sidebar__link${isActive(pathname, item.match) ? " sidebar__link--active" : ""}`}
            >
              <span>{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </nav>

        {isAuthenticated ? (
          <>
            <div className="sidebar__user" onClick={() => setConfirmLogout(true)} title="Cerrar sesión">
              {fotoUrl ? (
                <img src={fotoUrl} alt={firstName} className="sidebar__avatar" style={{ objectFit: "cover" }} />
              ) : (
                <div className="sidebar__avatar">{initial}</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--on-dark)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {firstName}
                </div>
                <div style={{ fontSize: 12, color: "var(--on-dark-faint)" }}>{planLabel}</div>
              </div>
              <span style={{ color: "var(--on-dark-faint)", fontSize: 18 }}>⋯</span>
            </div>
          </>
        ) : (
          <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
            <Link to="/registro" className="btn btn-dark btn--sm btn--full">Únete gratis</Link>
            <Link to="/login" className="btn btn-ghost-light btn--sm btn--full">Iniciar sesión</Link>
          </div>
        )}
      </aside>

      {/* MOBILE TOPBAR */}
      <div className="app-topbar-mobile">
        <Link to="/dashboard" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
          <img src="/logo-psicodosis.png" alt="" style={{ width: 28, height: 28, objectFit: "contain" }} />
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 16, color: "var(--ink)" }}>PzicoDosis</span>
        </Link>
        <button onClick={() => setDrawerOpen(true)} className="navbar__hamburger" style={{ display: "inline-flex", color: "var(--ink)" }} aria-label="Abrir menú">
          <Menu size={22} />
        </button>
      </div>

      {/* MOBILE DRAWER */}
      {drawerOpen && (
        <div className="drawer-overlay" onClick={() => setDrawerOpen(false)}>
          <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <button className="drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Cerrar menú">
              <X size={18} />
            </button>
            {NAV.map((item) => (
              <Link key={item.to} to={item.to} className="drawer-link" onClick={() => setDrawerOpen(false)}>
                {item.icon} &nbsp;{item.label}
              </Link>
            ))}
            {isAuthenticated ? (
              <button
                onClick={() => { setDrawerOpen(false); setConfirmLogout(true); }}
                className="drawer-link"
                style={{ background: "none", border: "none", textAlign: "left", cursor: "pointer", color: "#d99a93" }}
              >
                Cerrar sesión
              </button>
            ) : (
              <Link to="/registro" className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => setDrawerOpen(false)}>
                Únete gratis →
              </Link>
            )}
          </div>
        </div>
      )}

      {/* MOBILE BOTTOM TABBAR */}
      <nav className="mobile-tabbar">
        {NAV.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`mobile-tabbar__link${isActive(pathname, item.match) ? " mobile-tabbar__link--active" : ""}`}
          >
            <span style={{ fontSize: 18 }}>{item.icon}</span>
            {item.label}
          </Link>
        ))}
      </nav>

      {/* MAIN */}
      <main className="app-main" style={maxWidth ? { maxWidth } : undefined}>
        {children}
      </main>

      {confirmLogout && (
        <ConfirmModal
          title="¿Cerrar sesión?"
          message="Puedes volver a entrar cuando quieras. Tu progreso se mantiene guardado."
          confirmLabel="Cerrar sesión"
          danger
          onConfirm={doLogout}
          onCancel={() => setConfirmLogout(false)}
        />
      )}
    </div>
  );
}
